import { PropsWithChildren } from 'react';
import styled from '@emotion/styled';
import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  AccordionSummaryProps,
  alpha,
  Typography,
  TypographyProps
} from '@mui/material';
import PolarisIcon from '@/components/_common/PolarisIcon';

// Main Component
const ScheduleSelectedAccordion = ({ children, disableGutters = true, ...rest }: AccordionProps) => {
  return (
    <StyledAccordion {...rest} disableGutters={disableGutters}>
      {children}
    </StyledAccordion>
  );
};

export default ScheduleSelectedAccordion;

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(2),
  border: `1px solid ${alpha('#4C4E5CB2', 0.7)}`,
  boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
  '&.MuiPaper-root.MuiAccordion-root:last-of-type': {
    borderRadius: 8
  },
  '& .MuiCollapse-root .MuiAccordionDetails-root': {
    padding: theme.spacing(2, 0, 0)
  }
}));

// Summary
const Summary = ({ children, expandIcon, ...rest }: AccordionSummaryProps) => {
  return (
    <StyledSummary {...rest} expandIcon={expandIcon !== undefined ? expandIcon : <PolarisIcon icon="down" height={12} />}>
      {children}
    </StyledSummary>
  );
};

const StyledSummary = styled(AccordionSummary)(({ theme }) => ({
  '&.MuiAccordionSummary-root.MuiAccordionSummary-root': {
    flexDirection: 'row',
    '& .MuiAccordionSummary-content': {
      flexDirection: 'column',
      gap: theme.spacing(1)
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      alignSelf: 'flex-start',
      '&.Mui-expanded': {
        transform: 'rotate(180deg)'
      }
    }
  }
}));

// Content
const Content = ({ children }: PropsWithChildren) => {
  return <SummaryContent>{children}</SummaryContent>;
};

const SummaryContent = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  '& .MuiTypography-root:first-of-type': {
    color: '#D9D9D9'
  },
  '& .MuiTypography-root:last-child': {
    fontSize: theme.typography.pxToRem(14),
    color: alpha(theme.palette.text.primary, 0.5)
  }
}));

// SummaryTitle
const SummaryTitle = ({ children, ...rest }: TypographyProps) => {
  return <Typography {...rest}>{children}</Typography>;
};

// SummaryCaption
const SummaryCaption = ({ children, ...rest }: TypographyProps) => {
  return <Typography {...rest}>{children}</Typography>;
};

// SummaryChips
const SummaryChips = ({ children }: PropsWithChildren) => {
  return <SummaryChipsContainer>{children}</SummaryChipsContainer>;
};

const SummaryChipsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap'
}));

// Details
const Details = ({ children }: PropsWithChildren) => {
  return <StyledDetails>{children}</StyledDetails>;
};

const StyledDetails = styled(AccordionDetails)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  maxHeight: 200,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  scrollBehavior: 'smooth'
}));

// Exported compound components
ScheduleSelectedAccordion.Summary = Summary;
ScheduleSelectedAccordion.Content = Content;
ScheduleSelectedAccordion.Title = SummaryTitle;
ScheduleSelectedAccordion.Caption = SummaryCaption;
ScheduleSelectedAccordion.Chips = SummaryChips;
ScheduleSelectedAccordion.Details = Details;
